import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement } from '../../core/types';
import {
    solveWallRenderGeometry,
    WallEndpointGeometry,
} from '../../utils/WallGeometryUtils';

/** Joint relocation driving a stretch: old endpoint position -> new one. */
interface JointMove {
    from: THREE.Vector3;
    to: THREE.Vector3;
}

export type TrimMode = 'Corner' | 'Single' | 'Multiple';

export class TrimToolClass extends BaseTool {
    id = 'TRIM';
    name = 'Trim/Extend to Corner';
    cursor = 'crosshair';

    private mode: TrimMode;
    private boundaryElement: WallElement | null = null; // For Single/Multiple
    private firstElement: WallElement | null = null; // For Corner
    private firstPick: THREE.Vector3 | null = null; // Pick point on the first Corner element

    constructor(mode: TrimMode = 'Corner') {
        super();
        this.mode = mode;
        if (mode === 'Single') {
            this.id = 'TRIM_SINGLE';
            this.name = 'Trim/Extend Single Element';
        } else if (mode === 'Multiple') {
            this.id = 'TRIM_MULTIPLE';
            this.name = 'Trim/Extend Multiple Elements';
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.getWallIntersection(event);
        if (!hit) return;

        const { wall, point } = hit;

        if (this.mode === 'Corner') {
            this.handleCornerMode(wall, point);
        } else {
            this.handleExtendMode(wall, point);
        }
    }

    private handleCornerMode(wall: WallElement, point: THREE.Vector3) {
        if (!this.firstElement) {
            this.firstElement = wall;
            this.firstPick = point.clone();
            this.showWallHighlight(wall, '#ffaa00');
        } else {
            if (wall.id === this.firstElement.id) return;
            this.trimCorner(this.firstElement, this.firstPick ?? point.clone(), wall, point);
            this.firstElement = null;
            this.firstPick = null;
            this.context?.appStore.setToolPreview(null);
        }
    }

    private handleExtendMode(wall: WallElement, point: THREE.Vector3) {
        if (!this.boundaryElement) {
            this.boundaryElement = wall;
            this.showWallHighlight(wall, '#ffaa00');
        } else {
            if (wall.id === this.boundaryElement.id) return;

            this.extendToBoundary(this.boundaryElement, wall, point);

            if (this.mode === 'Single') {
                this.boundaryElement = null;
                this.context?.appStore.setToolPreview(null);
            }
            // For Multiple, we keep boundaryElement set and allow picking more
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    /** Push the dashed reference highlight into the viewport preview slot. */
    private showWallHighlight(wall: WallElement, color: string) {
        if (!this.context) return;
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        this.context.appStore.setToolPreview({
            type: 'wallHighlight',
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
            color
        });
    }

    private getWallIntersection(event: ThreeEvent<PointerEvent>) {
        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Walls');
        if (!hit) return null;
        return { wall: hit.element as WallElement, point: hit.point };
    }

    /** Snap tolerance (meters) — matches WallJoinSolver / WallGeometryUtils. */
    private static readonly JOINT_TOLERANCE = 0.01;

    /** Every wall currently in the model. */
    private collectWalls(): WallElement[] {
        if (!this.context) return [];
        return Array.from(this.context.bimStore.project.elements.values())
            .filter((e) => e.category === 'Walls') as WallElement[];
    }

    /**
     * 2D intersection of two wall centerlines treated as infinite lines,
     * projected at `baseZ`. Returns null for parallel / near-parallel lines.
     */
    private lineIntersection(
        aStart: THREE.Vector3,
        aEnd: THREE.Vector3,
        bStart: THREE.Vector3,
        bEnd: THREE.Vector3,
        baseZ: number
    ): THREE.Vector3 | null {
        const denom =
            (bEnd.y - bStart.y) * (aEnd.x - aStart.x) -
            (bEnd.x - bStart.x) * (aEnd.y - aStart.y);
        if (Math.abs(denom) < 1e-9) return null; // Parallel

        const ua =
            ((bEnd.x - bStart.x) * (aStart.y - bStart.y) -
                (bEnd.y - bStart.y) * (aStart.x - bStart.x)) /
            denom;

        return new THREE.Vector3(
            aStart.x + ua * (aEnd.x - aStart.x),
            aStart.y + ua * (aEnd.y - aStart.y),
            baseZ
        );
    }

    /**
     * Which endpoint of `wall` must travel to `intersection` so that the
     * portion the user clicked survives (BIM rule: keep the side you pick).
     *
     * Both the intersection and the pick point are projected onto the wall's
     * own axis (0 = start, 1 = end). Whatever side of the intersection the
     * pick falls on is the side that stays; the endpoint on the other side is
     * the one relocated. This also covers the open-corner case where the
     * intersection lies beyond the wall's end (t outside [0,1]): the clicked
     * body is entirely on the keep side, so the near end extends outward —
     * which is exactly what closes two walls at an acute angle into a tip.
     * Returns null when the move would collapse the wall.
     */
    private resolveMovingEndpoint(
        wall: WallElement,
        pick: THREE.Vector3,
        intersection: THREE.Vector3
    ): { key: 'start' | 'end'; at: THREE.Vector3; from: THREE.Vector3 } | null {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const axis = end.clone().sub(start);
        const lenSq = axis.lengthSq();
        if (lenSq < 1e-8) return null;

        const tInter = intersection.clone().sub(start).dot(axis) / lenSq;
        const tPick = pick.clone().sub(start).dot(axis) / lenSq;

        // Keep start (move end) when the pick is on the start side of the
        // intersection; keep end (move start) otherwise.
        const moveKey: 'start' | 'end' = tPick < tInter ? 'end' : 'start';
        const anchor = moveKey === 'end' ? start : end;

        // The corner must land on the moving side of the anchor, with the new
        // length staying above tolerance — otherwise the wall flips over its
        // own anchor or collapses, which rendered as broken geometry.
        const tAnchor = moveKey === 'end' ? 0 : 1;
        const margin = TrimToolClass.JOINT_TOLERANCE / Math.sqrt(lenSq);
        if (moveKey === 'end' && tInter <= tAnchor + margin) return null;
        if (moveKey === 'start' && tInter >= tAnchor - margin) return null;

        const from = moveKey === 'start' ? start : end;

        // Zero move: already at the corner. Still reported so the caller
        // re-solves the joint profiles against the current neighbours.
        if (from.distanceTo(intersection) < TrimToolClass.JOINT_TOLERANCE) {
            return { key: moveKey, at: from.clone(), from };
        }

        // Keep the endpoint's own base Z: walls on different base offsets must
        // not inherit the other wall's elevation through the corner point.
        return {
            key: moveKey,
            at: new THREE.Vector3(intersection.x, intersection.y, from.z),
            from,
        };
    }

    /**
     * Rewrite the endpoints stored in a wall's geometryData copy.
     */
    private withMovedEndpoint(
        wall: WallElement,
        move: { key: 'start' | 'end'; at: THREE.Vector3 }
    ): WallEndpointGeometry {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);
        const target = move.at;

        return {
            start: move.key === 'start' ? [target.x, target.y, target.z] : [start.x, start.y, start.z],
            end: move.key === 'end' ? [target.x, target.y, target.z] : [end.x, end.y, end.z],
        };
    }

    /**
     * Commit a trim/extend: relocate the picked endpoints, drag every wall that
     * shared a relocated joint along with it (stretch), and re-solve the render
     * geometry of all affected walls in a single batch.
     *
     * Walls are rendered from the baked `layerProfiles` polygons, not from
     * start/end. Writing the endpoints alone left the previous miters in place,
     * which is exactly the corruption seen at the joins after Trim/Extend;
     * re-solving every touched wall — including the neighbours that were not
     * themselves cut — is what makes the joins consistent.
     */
    private applyJointMove(
        direct: Map<string, WallEndpointGeometry>,
        joints: JointMove[]
    ): void {
        if (!this.context || direct.size === 0) return;

        const allWalls = this.collectWalls();
        const tol = TrimToolClass.JOINT_TOLERANCE;
        const endpoints = new Map(direct);

        // Connected walls stretch: an endpoint sitting on a relocated joint
        // follows the new position, the far end stays on its own alignment.
        for (const wall of allWalls) {
            if (endpoints.has(wall.id)) continue;
            const start = new THREE.Vector3(...wall.geometryData.start);
            const end = new THREE.Vector3(...wall.geometryData.end);

            const hitStart = joints.find((j) => start.distanceTo(j.from) < tol);
            const hitEnd = joints.find((j) => end.distanceTo(j.from) < tol);
            if (!hitStart && !hitEnd) continue;

            endpoints.set(wall.id, {
                start: hitStart
                    ? [hitStart.to.x, hitStart.to.y, hitStart.to.z]
                    : [start.x, start.y, start.z],
                end: hitEnd
                    ? [hitEnd.to.x, hitEnd.to.y, hitEnd.to.z]
                    : [end.x, end.y, end.z],
            });
        }

        // Re-solve every affected wall against the POST-move world: a
        // stretched neighbour must see the relocated joints of the others,
        // otherwise its miter is computed against stale geometry and the
        // joins disagree at render time.
        const postMoveWalls = allWalls.map((w) => {
            const geometry = endpoints.get(w.id);
            if (!geometry) return w;
            return { ...w, geometryData: { ...w.geometryData, ...geometry } } as WallElement;
        });

        const batch = Array.from(endpoints.entries()).flatMap(([id, geometry]) => {
            const wall = allWalls.find((w) => w.id === id);
            if (!wall) return [];
            return [{ id, updates: { geometryData: solveWallRenderGeometry(wall, geometry, postMoveWalls) } }];
        });

        if (batch.length === 0) return;
        this.context.bimStore.updateElements(batch);
    }

    private trimCorner(
        wall1: WallElement,
        pick1: THREE.Vector3,
        wall2: WallElement,
        pick2: THREE.Vector3
    ) {
        const s1 = new THREE.Vector3(...wall1.geometryData.start);
        const e1 = new THREE.Vector3(...wall1.geometryData.end);
        const s2 = new THREE.Vector3(...wall2.geometryData.start);
        const e2 = new THREE.Vector3(...wall2.geometryData.end);

        // The corner is where the two centerlines meet; each wall keeps the
        // side it was clicked on, so the shared vertex is solved once and
        // applied to both — that guarantees the two miters are identical.
        const intersection = this.lineIntersection(s1, e1, s2, e2, s1.z);
        if (!intersection) return;

        const move1 = this.resolveMovingEndpoint(wall1, pick1, intersection);
        const move2 = this.resolveMovingEndpoint(wall2, pick2, intersection);
        if (!move1 || !move2) return;

        const geom1 = this.withMovedEndpoint(wall1, move1);
        const geom2 = this.withMovedEndpoint(wall2, move2);

        const direct = new Map<string, WallEndpointGeometry>([
            [wall1.id, geom1],
            [wall2.id, geom2],
        ]);

        const joints: JointMove[] = [
            { from: move1.from, to: new THREE.Vector3(...(move1.key === 'start' ? geom1.start : geom1.end)) },
            { from: move2.from, to: new THREE.Vector3(...(move2.key === 'start' ? geom2.start : geom2.end)) },
        ];

        this.applyJointMove(direct, joints);
    }

    private extendToBoundary(boundary: WallElement, target: WallElement, pick: THREE.Vector3) {
        const bStart = new THREE.Vector3(...boundary.geometryData.start);
        const bEnd = new THREE.Vector3(...boundary.geometryData.end);
        const tStart = new THREE.Vector3(...target.geometryData.start);
        const tEnd = new THREE.Vector3(...target.geometryData.end);

        // The boundary keeps its own length (BIM never stretches the edge you
        // picked as the cutting limit); only the target reaches out to it.
        const intersection = this.lineIntersection(tStart, tEnd, bStart, bEnd, tStart.z);
        if (!intersection) return;

        const move = this.resolveMovingEndpoint(target, pick, intersection);
        if (!move) return;

        const geom = this.withMovedEndpoint(target, move);
        const direct = new Map<string, WallEndpointGeometry>([[target.id, geom]]);
        const joints: JointMove[] = [
            { from: move.from, to: new THREE.Vector3(...(move.key === 'start' ? geom.start : geom.end)) },
        ];

        // The boundary and its neighbours must be re-solved too: their miters
        // were computed against the target's old, shorter centerline.
        [boundary.id, ...this.collectWalls()
            .filter((w) => w.id !== target.id && w.id !== boundary.id)
            .filter((w) => {
                const s = new THREE.Vector3(...w.geometryData.start);
                const e = new THREE.Vector3(...w.geometryData.end);
                return s.distanceTo(bStart) < TrimToolClass.JOINT_TOLERANCE ||
                    e.distanceTo(bStart) < TrimToolClass.JOINT_TOLERANCE ||
                    s.distanceTo(bEnd) < TrimToolClass.JOINT_TOLERANCE ||
                    e.distanceTo(bEnd) < TrimToolClass.JOINT_TOLERANCE;
            })
            .map((w) => w.id)]
            .forEach((id) => {
                if (direct.has(id)) return;
                const wall = this.context!.bimStore.project.elements.get(id) as WallElement | undefined;
                if (!wall) return;
                direct.set(id, {
                    start: [...wall.geometryData.start] as [number, number, number],
                    end: [...wall.geometryData.end] as [number, number, number],
                });
            });

        this.applyJointMove(direct, joints);
    }

    deactivate() {
        super.deactivate();
        this.firstElement = null;
        this.firstPick = null;
        this.boundaryElement = null;
        this.context?.appStore.setToolPreview(null);
    }
}
